import { Constants } from 'constants';

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {
  Typography,
  Spinner,
  Button,
  Tooltip,
  Grid,
  TextField,
  Slider,
} from '@genie-ui/react';
import { useMediaQuery } from '@genie-ui/react/hooks';
import cx from 'classnames';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InsightsSharpIcon from '@mui/icons-material/InsightsSharp';
import { toJpeg } from 'html-to-image';
import download from 'downloadjs';
import SnackbarComponent from 'components/atoms/snackbarcomponent';
import ConfigPath from 'configpath';
import GuageChart from 'components/atoms/guagechart';
import EmailDialog from 'components/organisms/emaildialog';
import AxisChart from 'components/atoms/axischart';
import DialogComponent from 'components/atoms/dialogcomponent';
import GravityLayout from 'components/atoms/gravitylayout';
import { calculateMethod, debounce, inputValue } from 'utils';
import Digital from 'assets/images/Digital-Framework.png';
import useIsFromConferenceApp from 'hooks/usePlatform';

import './Gaplite.scss';
import { useDispatch } from 'react-redux';
import { setUser } from 'stores/slice/auth';

const getKpis = () => {
  let tempObj = {};
  Object.keys(Constants.KPIs).forEach((key) => {
    tempObj = {
      ...tempObj,
      [key]: {
        ...Constants.KPIs[key],
        value: 0,
        tooltipVal: false,
        error: false,
      },
    };
  });
  return tempObj;
};

// Added new div for loader under body tag
const loaderDiv = document.createElement('div');
loaderDiv.setAttribute('id', 'gaplite-loader');
loaderDiv.style.position = 'fixed';
loaderDiv.style.top = 0;
loaderDiv.style.zIndex = 4;
document.body.appendChild(loaderDiv);

function Gaplite({
  actisDataObj,
  isInvitationRoute,
  initializeFullStory,
}) {
  const [KpiInfo, setKpiInfo] = useState(getKpis);
  const [totalScore, setTotalScore] = useState(0);
  const [firstPriority, setFirstPriority] = useState(KpiInfo.KPI_1);
  const [secondPriority, setSecondPriority] = useState(KpiInfo.KPI_2);
  const [showRightSection, setShowRightSection] = useState(true);
  const [showActions, setShowActions] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const overallScoreRef = useRef(null);
  const isResponsive = useMediaQuery('(min-width:900px)');
  const isFromConferenceApp = useIsFromConferenceApp();
  const [openDialog, setOpenDialog] = useState(false);
  const [modifiedActisObject, setModifiedActisObject] = useState(actisDataObj || {});
  const dispatch = useDispatch();

  useEffect(() => {
    if (isInvitationRoute) {
      setOpenDialog(true);
    }
  }, [isInvitationRoute]);

  const closeDialog = (email) => () => {
    initializeFullStory(email);
    dispatch(setUser({ eventParticipantEmail: email }));
    setModifiedActisObject((prevState) => ({
      ...prevState,
      eventParticipantEmail: email,
    }));
    setOpenDialog(false);
  };

  useEffect(() => {
    if ((modifiedActisObject?.primaryEmail && modifiedActisObject?.primaryEmail !== '--') || modifiedActisObject?.eventParticipantEmail) {
      window.pushEvent?.(
        {
          ...Constants.ACTIS.LOGIN,
          ...modifiedActisObject,
        },
        Constants.LABELS.ACTIS_KEY_RAPID,
      );
    }
  }, [modifiedActisObject]);

  const pushSliderDetailsToActis = (actis, value, kpi) => (actis
      && window.pushEvent?.(
        {
          ...actis,
          activityValue: value?.toString(),
          activityAction: kpi,
        },
        Constants.LABELS.ACTIS_KEY_RAPID,
      ));

  const debouncedPushSliderDetailsToActis = useRef(
    debounce(
      (actis, value, kpi) => pushSliderDetailsToActis(actis, value, kpi),
      500,
    ),
  ).current;

  const setKpiDetails = (state, kpi, value, key, actis) => () => {
    if (key !== Constants.INPUT_TYPE.SNACKBAR) {
      debouncedPushSliderDetailsToActis({
        ...actis,
        ...modifiedActisObject,
      }, Number(value) >= 100 ? 100 : value, kpi);
    }
    state((item) => ({
      ...item,
      [kpi]: {
        ...item[kpi],
        value: Number(inputValue(value, item[kpi].value, Constants.INPUT_TYPE.SLIDER, '%', key)),
        tooltipVal: key === Constants.INPUT_TYPE.TOOLTIP ? value : false,
        error: !!(key === Constants.INPUT_TYPE.SLIDER
          && (Number(value) > 100 || Number(value) < 0)),
      },
    }));
  };

  const sliderChange = (kpi, actis) => (event) => {
    setShowActions(false);
    setKpiDetails(setKpiInfo, kpi, event.target.value, Constants.INPUT_TYPE.SLIDER, actis)();
  };

  const calculateKpiScore = () => calculateMethod(KpiInfo);

  const calculateScore = () => {
    setShowRightSection(false);
    setShowActions(true);
    const [first, second] = Object.values(KpiInfo).sort(
      (a, b) => a.value - b.value,
    );
    setFirstPriority(first);
    setSecondPriority(second);
    setTimeout(() => {
      setShowRightSection(true);
      setTotalScore(calculateKpiScore);
    }, 500);

    window.pushEvent?.(
      {
        ...Constants.ACTIS.CALCULATE,
        activityValue: (calculateKpiScore() / 20).toFixed(1).toString(),
        activityMeta: `KPI_Values: ${KpiInfo.KPI_1.value}, ${KpiInfo.KPI_2.value}, ${KpiInfo.KPI_3.value}, ${KpiInfo.KPI_4.value}, ${KpiInfo.KPI_5.value}`,
        ...modifiedActisObject,
      },
      Constants.LABELS.ACTIS_KEY_RAPID,
    );
    if (isResponsive) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } else {
      window.scrollTo(0, overallScoreRef.current.offsetTop - 77);
    }
  };

  const setStateVariables = (state, val, actis) => {
    state(val);
    return (actis
      && window.pushEvent?.(
        {
          ...actis,
          activityValue: val?.innerHTML ? 'true' : val.toString(),
          ...modifiedActisObject,
        },
        Constants.LABELS.ACTIS_KEY_RAPID,
      ));
  };

  const linkMonitoring = (actis, name) => (
    name
      ? window.pushEvent?.(
        {
          ...actis,
          activityValue: name,
          ...modifiedActisObject,
        },
        Constants.LABELS.ACTIS_KEY_RAPID,
      )
      : window.pushEvent?.(
        {
          ...actis,
          ...modifiedActisObject,
        },
        Constants.LABELS.ACTIS_KEY_RAPID,
      )
  );

  const printReport = () => {
    setShowLoader(true);
    const node = document.getElementById('root');
    toJpeg(node)
      .then((dataUrl) => {
        download(dataUrl, 'Rapid-Benchmarks-DES.jpg');
      })
      .catch(() => {})
      .finally(() => {
        setShowLoader(false);
      });
    window.pushEvent?.(
      {
        ...Constants.ACTIS.DOWNLOAD_IMAGE,
        ...modifiedActisObject,
      },
      Constants.LABELS.ACTIS_KEY_RAPID,
    );
  };

  const openLink = (link, actisDataObject) => {
    window.open(ConfigPath.GARTNER_URL + link, isFromConferenceApp ? '_self' : '_blank');
    linkMonitoring(actisDataObject);
  };

  const startNow = () => {
    window.open(Constants.LINKS.DES_TOOL, '_blank');
    window.pushEvent?.(
      {
        ...Constants.ACTIS.DES_TOOL,
        ...modifiedActisObject,
      },
      Constants.LABELS.ACTIS_KEY_RAPID,
    );
  };

  return (
    <div>
      <Grid container className="root-des">
        {showLoader && (
          ReactDOM.createPortal(
            <div className="progress-des">
              <Spinner />
            </div>,
            document.getElementById('gaplite-loader'),
          )
        )}
        <div className="headerBox">
          <GravityLayout>
            <Grid container className="firstContainer-des">
              <Grid container className="benchmarkingGrid">
                <Typography className="containerHeader-des">
                  {Constants.SUBHEADER.HEADER}
                </Typography>
                {!isFromConferenceApp && (
                <Button
                  variant="ghost"
                  onClick={printReport}
                  className="printButton"
                  disableRipple
                >
                  {Constants.BUTTON.DOWNLOAD}
                  <FileDownloadIcon />
                </Button>
                )}
              </Grid>
              <Grid container className="container-heading">
                <Typography gutterBottom className="subheader">
                  {Constants.SUBHEADER.SUBHEADER_1}
                  <Button
                    variant="ghost"
                    className="readMore"
                    onClick={() => openLink(
                      Constants.LINKS.DIGITAL_SCORECARD,
                      Constants.ACTIS.DES_LINK,
                    )}
                  >
                    {Constants.LABELS.TAKE_FULL_2}
                  </Button>
                </Typography>
              </Grid>
            </Grid>
          </GravityLayout>
        </div>
        <div className="descriptionBox">
          <GravityLayout>
            <Grid container className="secondContainer-des">
              <Grid item className="leftRightDiv">
                {Object.keys(Constants.KPIs)?.map((item, index) => (
                  <Grid container className="kpiContainer" key={item}>
                    <div>
                      <Typography className="kpiHeader">
                        {`${index + 1}. ${Constants.KPIs[item].HEADER}`}
                        <Tooltip
                          open={KpiInfo[item]?.tooltipVal}
                          className="tooltip-des"
                          onClose={setKpiDetails(
                            setKpiInfo,
                            item,
                            false,
                            Constants.INPUT_TYPE.TOOLTIP,
                            Constants.ACTIS.TOOLTIP_CLOSE,
                          )}
                          title={<Typography>{Constants.KPIs[item]?.TOOLTIP}</Typography>}
                          arrow
                        >
                          <InfoOutlinedIcon className="infoIcon" onMouseEnter={setKpiDetails(setKpiInfo, item, true, Constants.INPUT_TYPE.TOOLTIP, Constants.ACTIS.TOOLTIP_OPEN)} />
                        </Tooltip>
                      </Typography>
                      <Typography className="kpiSubheader">
                        {Constants.KPIs[item].SUBHEADER}
                      </Typography>
                    </div>
                    <div className="sliderDiv">
                      <Slider
                        step={1}
                        valueLabelDisplay="auto"
                        className="slider"
                        marks={Constants.MARKS}
                        value={KpiInfo[item]?.value}
                        onChange={sliderChange(item, Constants.ACTIS.SLIDER)}
                        valueLabelFormat={(val) => `${val}%`}
                      />
                      <TextField
                        variant="outlined"
                        placeholder="0"
                        className="inputField-des"
                        type="number"
                        onChange={sliderChange(item, Constants.ACTIS.INPUT_FIELD)}
                        value={
                          KpiInfo[item]?.value !== Constants.VALUES.DEFAULT_VALUE
                            ? KpiInfo[item]?.value
                            : ''
                        }
                        InputProps={{
                          endAdornment: '%',
                        }}
                      />
                    </div>
                    <SnackbarComponent
                      msg={Constants.ERROR.RANGE_ERROR}
                      onClose={
                        setKpiDetails(setKpiInfo, item, false, Constants.INPUT_TYPE.SNACKBAR)
                      }
                      variant={Constants.SNACKBAR.ERROR}
                      open={KpiInfo[item]?.error}
                    />
                  </Grid>
                ))}
                <Button
                  variant="primary"
                  className="calculateButton"
                  onClick={calculateScore}
                >
                  {Constants.BUTTON.CALCULATE}
                </Button>
              </Grid>
              <Grid
                item
                className="leftRightDiv"
              >
                <div className={cx('scoreDiv', {
                  animationDiv: showRightSection,
                })}
                >
                  <div ref={overallScoreRef}>
                    <Typography className="kpiText">
                      {Constants.LABELS.OVERALL_SCORE}
                    </Typography>
                    <GuageChart
                      percentage={totalScore}
                      showCount={showRightSection}
                    />
                    <Typography gutterBottom className="overallText">
                      {Constants.LABELS.TAKE_FULL_1}
                      <Button
                        variant="ghost"
                        onClick={() => openLink(
                          Constants.LINKS.DIGITAL_SCORECARD,
                          Constants.ACTIS.DES_LINK,
                        )}
                        className="digitalScoreLink"
                      >
                        {Constants.LABELS.TAKE_FULL_2}
                      </Button>
                      {Constants.LABELS.TAKE_FULL_3}
                      <Button
                        variant="ghost"
                        onClick={() => setStateVariables(
                          setOpenModal,
                          true,
                          Constants.ACTIS.DESF_OPEN,
                        )}
                        className="digitalScoreLink"
                      >
                        {Constants.LABELS.TAKE_FULL_4}
                      </Button>
                    </Typography>
                    <DialogComponent
                      open={openModal}
                      handleClose={() => setStateVariables(
                        setOpenModal,
                        false,
                        Constants.ACTIS.DESF_CLOSE,
                      )}
                      content={(
                        <img
                          alt="framework"
                          src={Digital}
                          className="sliderDiv"
                        />
                      )}
                    />
                  </div>
                  <div>
                    <Typography className="kpiText">
                      {Constants.LABELS.TOP_PRIORITY}
                    </Typography>
                  </div>

                  <EmailDialog
                    openDialog={openDialog}
                    handleClose={closeDialog}
                  />
                  {[firstPriority, secondPriority].map((priority) => (
                    <Grid item key={priority.HEADER}>
                      <Typography className="firstText">
                        {priority.HEADER}
                      </Typography>
                      <AxisChart
                        value={`${priority.value}%`}
                        maxLimit={priority.upperMargin}
                      />
                      {showActions && (
                        <>
                          <div>
                            <Typography className="actions">
                              {Constants.LABELS.SUGGESTED_ACTION}
                            </Typography>
                            <ul className="ul">
                              {(priority.upperMargin >= priority.value
                                ? priority.ACTION_LESS
                                : priority.ACTION_MORE
                              ).map((item) => (
                                <li className="list" key={item}>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <Typography className="actions">
                              {Constants.LABELS.RECOMMENDED_RESEARCH}
                            </Typography>
                            <ul className="ul">
                              {(priority.upperMargin >= priority.value
                                ? priority.RESEARCH_LESS
                                : priority.RESEARCH_MORE
                              ).map((item) => (
                                <li className="list" key={item.name}>
                                  <a
                                    className="link"
                                    href={ConfigPath.GARTNER_URL + item.link}
                                    target={!isFromConferenceApp ? '_blank' : '_self'}
                                    rel="noopener noreferrer"
                                    onClick={() => linkMonitoring(
                                      Constants.ACTIS.RECOMMENDED_RESEARCH,
                                      `${priority.HEADER} - ${item.name} | ${item.code}`,
                                    )}
                                  >
                                    {item.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </>
                      )}
                    </Grid>
                  ))}
                  {showActions && (
                    <Button
                      className="completeFull"
                      variant="secondary"
                      onClick={() => openLink(
                        Constants.LINKS.DIGITAL_SCORECARD,
                        Constants.ACTIS.CDES,
                      )}
                    >
                      {Constants.BUTTON.COMPLETE_FULL}
                    </Button>
                  )}
                </div>
              </Grid>
              <Grid item className="readyForHrDiv">
                <InsightsSharpIcon className="readyForHrIcon" />
                <div className="readyForDiv">
                  <Typography className="readyForHrHeading">
                    {Constants.READY.HEADING}
                  </Typography>
                  <Typography>
                    {Constants.READY.TEXT_1}
                    <b>
                      {Constants.READY.TEXT_2}
                    </b>
                    {Constants.READY.TEXT_3}
                  </Typography>
                </div>
                <Button
                  onClick={startNow}
                  variant={showActions ? 'primary' : 'secondary'}
                >
                  {Constants.BUTTON.SEE_MORE}
                </Button>
              </Grid>
              <Grid item>
                {Constants.BOTTOM_HELPER.map((item) => (
                  <Typography
                    gutterBottom
                    className="bottomText"
                    key={item.text1}
                  >
                    {item.text1}
                    {item.text2 && (
                      <Button
                        variant="ghost"
                        onClick={() => openLink(
                          Constants.LINKS.DIGITAL_SCORECARD,
                          Constants.ACTIS.DES_LINK,
                        )}
                        className="digitalScoreLink"
                      >
                        {item.text2}
                      </Button>
                    )}
                    {item.text3}
                  </Typography>
                ))}
              </Grid>
            </Grid>
          </GravityLayout>
        </div>
      </Grid>
    </div>
  );
}

Gaplite.propTypes = {
  actisDataObj: PropTypes.instanceOf(Object),
  isInvitationRoute: PropTypes.bool,
  initializeFullStory: PropTypes.func,
};

Gaplite.defaultProps = {
  actisDataObj: {},
  isInvitationRoute: false,
  initializeFullStory: () => {},
};

export default Gaplite;
